import React, { useEffect, useState } from "react";

// Module-level cache for the site-wide schema bundle so we only fetch once
let _schemaStackCache = null;
let _schemaStackPromise = null;

async function fetchSchemaStack(api) {
    if (_schemaStackCache) return _schemaStackCache;
    if (_schemaStackPromise) return _schemaStackPromise;
    const url = `${api}/api/seo/schema-stack`;
    _schemaStackPromise = fetch(url)
        .then((r) => (r.ok ? r.json() : []))
        .then((json) => {
            _schemaStackCache = Array.isArray(json) ? json : [];
            return _schemaStackCache;
        })
        .catch(() => {
            _schemaStackCache = [];
            return _schemaStackCache;
        });
    return _schemaStackPromise;
}

/**
 * SEOHead — declarative per-page meta + JSON-LD injection (no react-helmet needed).
 *
 * Props:
 *   title          page title (≤60 chars recommended)
 *   description    meta description (≤160 chars)
 *   keywords       array of strings
 *   canonical      path or full URL (omit to auto-use current href)
 *   image          OG image (path or full URL)
 *   type           "website" | "article" | "product"
 *   jsonLd         single JSON-LD object OR array of them — stacked on top of site bundle
 *   breadcrumbs    array of { name, path } — auto-converted to BreadcrumbList schema
 *   publishedAt    ISO date for article:published_time
 *   author         author name
 *   noindex        true → emit <meta name="robots" content="noindex,nofollow">
 *   includeSiteSchemas  default true → fetches /api/seo/schema-stack and stacks Organization+WebSite+SoftwareApp+FAQ
 */
export default function SEOHead({
    title,
    description,
    keywords,
    canonical,
    image,
    type = "website",
    jsonLd = null,
    breadcrumbs = null,
    publishedAt,
    author,
    noindex = false,
    includeSiteSchemas = true,
}) {
    const [siteSchemas, setSiteSchemas] = useState([]);
    const API = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (!includeSiteSchemas) return;
        let cancelled = false;
        fetchSchemaStack(API).then((s) => { if (!cancelled) setSiteSchemas(s); });
        return () => { cancelled = true; };
    }, [API, includeSiteSchemas]);

    useEffect(() => {
        const base = window.location.origin;
        const cUrl = canonical ? (canonical.startsWith("http") ? canonical : base + canonical) : window.location.href;
        const ogImage = image ? (image.startsWith("http") ? image : base + image) : `${base}/api/marketing/file/01-storefront-hero-desktop.jpg`;

        const created = [];
        const setMeta = (attr, key, content) => {
            if (!content) return;
            let m = document.querySelector(`meta[${attr}="${key}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); created.push(m); }
            m.setAttribute("content", content);
        };

        const prevTitle = document.title;
        if (title) document.title = title;

        setMeta("name", "description", description);
        if (keywords?.length) setMeta("name", "keywords", keywords.join(", "));
        if (author) setMeta("name", "author", author);
        if (noindex) setMeta("name", "robots", "noindex,nofollow");
        else setMeta("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1");

        // OpenGraph
        setMeta("property", "og:type", type);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description);
        setMeta("property", "og:url", cUrl);
        setMeta("property", "og:image", ogImage);
        setMeta("property", "og:image:width", "1200");
        setMeta("property", "og:image:height", "630");
        setMeta("property", "og:site_name", "CharityFundraiser");
        setMeta("property", "og:locale", "en_US");
        if (publishedAt) setMeta("property", "article:published_time", publishedAt);

        // Twitter
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:site", "@charityfundraiser");
        setMeta("name", "twitter:title", title);
        setMeta("name", "twitter:description", description);
        setMeta("name", "twitter:image", ogImage);

        // Canonical
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); created.push(link); }
        link.setAttribute("href", cUrl);

        // Build the stacked JSON-LD payload
        const allSchemas = [];
        if (includeSiteSchemas) allSchemas.push(...siteSchemas);
        if (jsonLd) {
            if (Array.isArray(jsonLd)) allSchemas.push(...jsonLd);
            else allSchemas.push(jsonLd);
        }
        if (breadcrumbs?.length) {
            allSchemas.push({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": breadcrumbs.map((b, i) => ({
                    "@type": "ListItem",
                    "position": i + 1,
                    "name": b.name,
                    "item": b.path?.startsWith("http") ? b.path : base + (b.path || "/"),
                })),
            });
        }

        let script = null;
        if (allSchemas.length) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.textContent = JSON.stringify(allSchemas);
            document.head.appendChild(script);
        }

        return () => {
            document.title = prevTitle;
            created.forEach(n => n.remove());
            if (script) script.remove();
        };
    }, [title, description, keywords, canonical, image, type, jsonLd, breadcrumbs, publishedAt, author, noindex, includeSiteSchemas, siteSchemas, API]);

    return null;
}
